import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Check, Hexagon, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrutalButton } from "@/components/dashboard/BentoCard";
import { MarketingShell } from "@/components/marketing/MarketingShell";

export const PRICING = {
  root: "pricing-page",
  tierCard: (id) => `pricing-tier-${id}`,
  tierBtn: (id) => `pricing-cta-${id}`,
};

const TIER_META = {
  scout: {
    name: "Scout",
    tagline: "Solo researcher — one metro at a time.",
    features: [
      "Full nationwide database",
      "Unlimited filters + city search",
      "CSV + JSON export",
      "30-minute auto-refresh",
    ],
    accent: "bg-white",
  },
  tracker: {
    name: "Tracker",
    tagline: "Small team — three months of coverage in one shot.",
    features: [
      "Everything in Scout",
      "Save $4/month vs monthly",
      "Priority auto-refresh",
      "Multi-metro dashboards",
    ],
    accent: "bg-yellow-50 border-yellow-500",
    highlight: "MOST POPULAR",
  },
  investor: {
    name: "Investor",
    tagline: "Serious player — the whole year, one price.",
    features: [
      "Everything in Tracker",
      "Save $45/year vs monthly",
      "Early access to new adapters",
      "Watchlist alerts (coming soon)",
    ],
    accent: "bg-white",
    highlight: "BEST VALUE",
  },
};

function formatPrice(cents, interval, interval_count) {
  const dollars = (cents / 100).toFixed(2);
  const label =
    interval === "year"
      ? "/year"
      : interval_count === 3
      ? "/3 months"
      : "/month";
  return { dollars: `$${dollars}`, unit: label };
}

export default function PricingPage() {
  const { user, isAuthenticated, subscription } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["billing-plans"],
    queryFn: async () => (await api.get("/billing/plans")).data,
  });

  const plans = data?.plans || [];

  const startCheckout = useMutation({
    mutationFn: async (planId) => {
      const origin = window.location.origin;
      const res = await api.post("/billing/checkout", { plan: planId, origin_url: origin });
      return res.data;
    },
    onSuccess: (payload) => {
      if (payload.checkout_url) {
        window.location.href = payload.checkout_url;
      } else {
        toast.error("Could not start checkout.");
      }
    },
    onError: (err) => {
      const raw = err?.response?.data?.detail;
      const detail =
        typeof raw === "string"
          ? raw
          : Array.isArray(raw)
          ? raw.map((e) => e?.msg || JSON.stringify(e)).join("; ")
          : raw
          ? JSON.stringify(raw)
          : null;
      if (err?.response?.status === 401) {
        toast.error("Please sign in first.");
        navigate("/login");
        return;
      }
      toast.error(detail || err.message || "Checkout failed");
    },
    onSettled: () => setPending(null),
  });

  const handleClick = (planId) => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    setPending(planId);
    startCheckout.mutate(planId);
  };

  const trialDays = data?.trial_days ?? 3;

  return (
    <MarketingShell>
    <div data-testid={PRICING.root} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 border-2 border-black bg-yellow-300 font-mono-data text-[10px] uppercase tracking-widest">
          <Sparkles size={12} /> {trialDays}-day free trial · cancel anytime
        </div>
        <h1 className="mt-6 font-display text-4xl md:text-5xl font-bold tracking-tight text-red-600">
          Pick your beat.
        </h1>
        <p className="mt-4 text-zinc-600">
          Every plan unlocks the full nationwide database of current and upcoming
          delinquent tax properties — down to owner, parcel, and amount owed.
        </p>
        {user ? (
          <p className="mt-3 text-xs font-mono-data text-zinc-500 uppercase tracking-widest">
            Signed in as {user.email}
          </p>
        ) : null}
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {isLoading && (
          <div
            data-testid="pricing-loading"
            className="md:col-span-3 border-2 border-black bg-white p-8 text-center font-mono-data text-sm text-zinc-500 uppercase tracking-widest"
          >
            Loading plans…
          </div>
        )}
        {isError && (
          <div
            data-testid="pricing-error"
            className="md:col-span-3 border-2 border-red-600 bg-red-50 p-8 text-center"
          >
            <div className="font-display font-bold text-lg">Couldn't load plans.</div>
            <p className="text-sm text-zinc-600 mt-1">
              Check your connection and try again.
            </p>
            <button
              data-testid="pricing-retry"
              onClick={() => refetch()}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 border-2 border-black bg-yellow-400 font-display font-semibold text-sm shadow-brutal-sm hover:bg-yellow-300"
            >
              Try again
            </button>
          </div>
        )}
        {!isLoading && !isError && plans.length === 0 && (
          <div
            data-testid="pricing-empty"
            className="md:col-span-3 border-2 border-black bg-white p-8 text-center font-mono-data text-sm text-zinc-500 uppercase tracking-widest"
          >
            No plans configured.
          </div>
        )}
        {plans.map((p) => {
          const meta = TIER_META[p.id];
          const price = formatPrice(p.unit_amount, p.interval, p.interval_count);
          const isPending = pending === p.id;
          const alreadyOn = subscription?.plan === p.id && subscription?.active;
          return (
            <div
              key={p.id}
              data-testid={PRICING.tierCard(p.id)}
              className={`relative border-2 border-black p-8 ${meta.accent} shadow-brutal flex flex-col`}
            >
              {meta.highlight ? (
                <div className="absolute -top-3 left-6 bg-red-600 text-white px-3 py-1 border-2 border-black font-mono-data text-[10px] uppercase tracking-widest">
                  {meta.highlight}
                </div>
              ) : null}
              <div className="flex items-center gap-2">
                <Hexagon
                  size={28}
                  className="text-yellow-500 fill-yellow-100"
                  strokeWidth={2}
                />
                <div className="font-display font-bold text-2xl tracking-tight">
                  {meta.name}
                </div>
              </div>
              <p className="mt-2 text-sm text-zinc-600 min-h-[40px]">{meta.tagline}</p>
              <div className="mt-6 flex items-end gap-1">
                <div className="font-display text-5xl font-bold tracking-tight">
                  {price.dollars}
                </div>
                <div className="pb-2 font-mono-data text-sm text-zinc-500">{price.unit}</div>
              </div>
              <ul className="mt-6 space-y-2 flex-1">
                {meta.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check size={14} className="mt-1 text-emerald-600" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <BrutalButton
                data-testid={PRICING.tierBtn(p.id)}
                onClick={() => handleClick(p.id)}
                disabled={isPending || alreadyOn}
                className="mt-8 w-full justify-center py-3"
                variant={meta.highlight ? "primary" : "secondary"}
              >
                {alreadyOn
                  ? "Current plan"
                  : isPending
                  ? "Redirecting…"
                  : `Start ${trialDays}-day free trial`}
              </BrutalButton>
              <p className="mt-3 text-[11px] text-zinc-500 text-center">
                No charge for {trialDays} days. Cancel anytime.
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-10 text-center text-xs font-mono-data text-zinc-500 uppercase tracking-widest">
        Powered by Stripe · secure card processing · manage or cancel from your account
      </div>
    </div>
    </MarketingShell>
  );
}
